import React from 'react'
import { Button } from '@/shared/components/ui'
import { useTranslation } from '@/shared/contexts/TranslationContext'

export interface BulkAction {
  key: string
  label: string
  icon?: React.ReactNode
  variant?: 'primary' | 'secondary' | 'danger'
  onClick: () => void
  disabled?: boolean
}

interface BulkActionsBarProps {
  selectedCount: number
  totalCount: number
  actions: BulkAction[]
  onClear: () => void
  loading?: boolean
}

export function BulkActionsBar({
  selectedCount,
  totalCount,
  actions,
  onClear,
  loading = false
}: BulkActionsBarProps) {
  if (selectedCount === 0) return null

  const isAllSelected = selectedCount === totalCount
  const { t } = useTranslation()

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-slide-up">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center gap-6">
        {/* Selection indicator */}
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30">
            <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900 dark:text-white">
              {selectedCount} {selectedCount === 1 ? t('list.selectedCountOne', { count: String(selectedCount) }) : t('list.selectedCountMany', { count: String(selectedCount) })}
            </div>
            {isAllSelected && (
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {t('list.allItemsSelected')}
              </div>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="h-10 w-px bg-slate-200 dark:bg-slate-700" />

        {/* Actions */}
        <div className="flex items-center gap-2">
          {actions.map((action) => (
            <Button
              key={action.key}
              variant={action.variant || 'secondary'}
              onClick={action.onClick}
              disabled={action.disabled || loading}
              className="px-3 py-2 text-sm"
            >
              {action.icon && (
                <span className="inline-flex mr-2">{action.icon}</span>
              )}
              {action.label}
            </Button>
          ))}
        </div>

        {/* Clear selection button */}
        <button
          onClick={onClear}
          disabled={loading}
          className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:text-slate-300 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title={t('list.clearSelection')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  )
}
